import React, { ReactNode } from 'react';

const CodeBlock = ({ code, language, position }: { code: ReactNode, language: string, position: string }) => {
  return (
    <div className="w-[400px] pointer-events-none select-none">
      <div className="bg-black/10 backdrop-blur-sm rounded-lg p-4 border border-white/5">
      <pre className="font-mono text-sm">
        <code>
          {code}
        </code>
      </pre>
      </div>
    </div>
  );
};

const BackgroundCode = () => {
  const codeSnippets = {
    topLeft: `
// Initialize Application
function initApp() {
  const config = {
    theme: 'dark',
    mode: 'production',
    features: {
      animations: true,
      particles: true,
      glassmorphism: true
    }
  };
  
  setupTheme(config.theme);
  initializeFeatures(config.features);
  return setup(config);
}

function setupTheme(theme) {
  document.documentElement.className = theme;
  return {
    success: true,
    timestamp: new Date()
  };
}`,
    topRight: `
// User Interface Types
interface User {
  id: string;
  name: string;
  role: UserRole;
  preferences: UserPreferences;
  stats: UserStats;
}

type UserRole = {
  access: string[];
  level: number;
  permissions: {
    create: boolean;
    update: boolean;
    delete: boolean;
  }
};

interface UserPreferences {
  theme: 'light' | 'dark';
  notifications: boolean;
  language: string;
}

interface UserStats {
  lastLogin: Date;
  loginCount: number;
  activeTime: number;
}`,
    bottomLeft: `
/* Styling Configuration */
.container {
  display: grid;
  grid-template: repeat(3, 1fr);
  gap: 1.5rem;
  position: relative;
  z-index: 1;
}

.glass-card {
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  padding: 2rem;
  transition: all 0.3s ease;
}

.glass-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  background: rgba(255, 255, 255, 0.08);
}

.animated-border {
  position: relative;
  overflow: hidden;
}

.animated-border::before {
  content: '';
  position: absolute;
  background: linear-gradient(90deg, 
    rgba(255,255,255,0) 0%,
    rgba(255,255,255,0.1) 50%,
    rgba(255,255,255,0) 100%);
  animation: borderGlow 2s infinite;
}`,
    bottomRight: `
// Data Fetching and Error Handling
async function fetchData<T>(
  endpoint: string,
  options?: RequestOptions
): Promise<Result<T>> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      options?.timeout || 5000
    );

    const response = await fetch(endpoint, {
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers
      },
      ...options
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new ApiError(
        response.statusText,
        response.status
      );
    }

    const data = await response.json();
    return {
      success: true,
      data,
      metadata: {
        timestamp: new Date(),
        requestId: crypto.randomUUID()
      }
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error 
        ? error 
        : new Error('Unknown error')
    };
  }
}`
  };

  const formatCode = (code: string) => {
    return code.split('\n').map((line, index) => {
      // Comments
      if (line.trim().startsWith('//') || line.trim().startsWith('/*')) {
        return <span key={index} className="block text-gray-400">{line}</span>;
      }
      
      // Keywords
      line = line.replace(/(function|interface|type|const|return|async|await|try|catch|class|private|public|constructor|new|if|throw)/g, 
        '<span class="text-yellow-400">$1</span>');
      
      // Types and classes
      line = line.replace(/(User|UserRole|UserPreferences|UserStats|Service|IServiceProvider|ServiceProvider|WebDevelopment|UiDesign|CodeReview|Result|ApiError|RequestOptions)/g, 
        '<span class="text-blue-400">$1</span>');
      
      // Function names
      line = line.replace(/(initApp|setup|setupTheme|initializeServices|getService|registerService|fetchData)/g, 
        '<span class="text-green-400">$1</span>');
      
      // CSS properties and values
      line = line.replace(/(display|grid-template|gap|position|z-index|backdrop-filter|background|border|border-radius|padding|transition|transform|box-shadow|overflow|content|animation)/g, 
        '<span class="text-pink-400">$1</span>');

      return (
        <span 
          key={index} 
          className="block text-gray-200"
          dangerouslySetInnerHTML={{ __html: line }}
        />
      );
    });
  };

  return (
    <>
      {/* Top Section */}
      <div className="absolute top-0 w-full flex justify-between opacity-[0.15]">
        <div className="-ml-20">
          <CodeBlock 
            code={formatCode(codeSnippets.topLeft)} 
            language="javascript"
            position="topLeft"
          />
        </div>
        <div className="-mr-20">
          <CodeBlock 
            code={formatCode(codeSnippets.topRight)} 
            language="typescript"
            position="topRight"
          />
        </div>
      </div>

      {/* Bottom Section */}
      <div className="absolute bottom-0 w-full flex justify-between opacity-[0.15]">
        <div className="-ml-20">
          <CodeBlock 
            code={formatCode(codeSnippets.bottomLeft)} 
            language="css"
            position="bottomLeft"
          />
        </div>
        <div className="-mr-20">
          <CodeBlock 
            code={formatCode(codeSnippets.bottomRight)} 
            language="javascript"
            position="bottomRight"
          />
        </div>
      </div>
    </>
  );
};

export default BackgroundCode; 